import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Banner, Button, Checkbox, Field, PageHead, Segmented, StateBlock, useToast } from '@w7/ui'
import type { StaffUser } from '@/api/map/staffUsers'
import { authHeaders } from '@/auth/session'
import { cmApi } from '@/api/cmApi'
import {
  useCreateStaffUser,
  useStaffUser,
  useUpdateStaffUser,
} from '@/api/hooks/staff/useStaffUsers'
import {
  ACCESS_LEVEL_BANNER,
  labPrefixedStaffUsername,
  staffUserFormError,
  toggleStaffGrant,
} from '@/features/users/userForm'
import {
  featureKey,
  filterStaffFormPageFeatures,
  retainStaffFormSelections,
  roleslistToMenus,
  selectedFromAuthorise,
  selectedFromGrantCodes,
  selectedToAuthorise,
} from '@/features/users/fpAuthorise'
import './users.css'

const CM_ROLE = 'Company Master'

const SESSION_FLAGS: { id: StaffUser['bookmakerSessionFlag']; label: string }[] = [
  { id: 'all', label: 'All markets (match odds + bookmaker + session)' },
  { id: 'onlyBookmaker', label: 'Only bookmaker' },
  { id: 'onlySession', label: 'Only session / fancy' },
  { id: 'bookmakerSession', label: 'Only bookmaker & session (no match odds)' },
]

export function UserFormPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const toast = useToast()

  const roleslist = useQuery({
    queryKey: ['cm', 'staff', 'roleslists', CM_ROLE],
    queryFn: () => cmApi.staffRoleslistGet(authHeaders(), { roleName: CM_ROLE }),
  })
  const existing = useStaffUser(isNew ? undefined : id)
  const createMut = useCreateStaffUser()
  const updateMut = useUpdateStaffUser(id ?? '')
  const self = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
    enabled: isNew,
  })
  const ownerUsername = (self.data?.node?.username || self.data?.node?.name || '').trim()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [status, setStatus] = useState<'active' | 'inactive'>('active')
  const [sessionFlag, setSessionFlag] =
    useState<StaffUser['bookmakerSessionFlag']>('all')
  /** pageName\\0label keys — not grant codes (codes are shared across pages). */
  const [selected, setSelected] = useState<string[]>([])
  const [hydrated, setHydrated] = useState(false)

  const pageFeatures = useMemo(
    () => filterStaffFormPageFeatures(roleslist.data?.pageFeatures ?? []),
    [roleslist.data?.pageFeatures],
  )
  const menus = useMemo(() => roleslistToMenus(pageFeatures), [pageFeatures])

  useEffect(() => {
    if (hydrated) return
    if (isNew) {
      if (!roleslist.isSuccess) return
      setHydrated(true)
      return
    }
    if (existing.data && roleslist.isSuccess) {
      setUsername(existing.data.username)
      setStatus(existing.data.status)
      setSessionFlag(existing.data.bookmakerSessionFlag)
      const fromAuthorise = selectedFromAuthorise(existing.data.authorise)
      const hydratedKeys = fromAuthorise.length
        ? fromAuthorise
        : selectedFromGrantCodes(pageFeatures, existing.data.grants ?? [])
      setSelected(retainStaffFormSelections(hydratedKeys, pageFeatures))
      setHydrated(true)
    }
  }, [roleslist.isSuccess, existing.data, isNew, hydrated, pageFeatures])

  const setFeature = (pageName: string, label: string, on: boolean) => {
    setSelected((prev) => toggleStaffGrant(prev, featureKey(pageName, label), on))
  }

  const busy = createMut.isPending || updateMut.isPending
  /** Fail closed: never POST a bare suffix while hierarchy self is unresolved. */
  const createBlocked = isNew && !ownerUsername
  const saveDisabled = busy || createBlocked

  const onSave = async () => {
    if (createBlocked) {
      toast.error('Owner username required')
      return
    }
    const formErr = staffUserFormError({
      isNew,
      username,
      password,
      confirm,
      ownerUsername: isNew ? ownerUsername : undefined,
    })
    if (formErr) {
      toast.error(formErr)
      return
    }

    const authorise = selectedToAuthorise(pageFeatures, selected)

    if (isNew) {
      const fullUsername = labPrefixedStaffUsername(ownerUsername, username)
      if (!fullUsername) {
        toast.error('Owner username required')
        return
      }
      try {
        const created = await createMut.mutateAsync({
          username: fullUsername,
          password,
          status,
          bookmakerSessionFlag: sessionFlag,
          accessRoleName: CM_ROLE,
          authorise,
        })
        toast.success('User created')
        navigate(`/users/${created.id}`)
      } catch (err) {
        toast.error('Could not create user', err instanceof Error ? err.message : undefined)
      }
      return
    }

    try {
      await updateMut.mutateAsync({
        ...(password ? { password } : {}),
        status,
        bookmakerSessionFlag: sessionFlag,
        accessRoleName: CM_ROLE,
        authorise,
      })
      toast.success('User updated')
      setPassword('')
      setConfirm('')
    } catch (err) {
      toast.error('Could not update user', err instanceof Error ? err.message : undefined)
    }
  }

  if (!isNew && existing.isLoading) {
    return <StateBlock icon="users" title="Loading user…" />
  }
  if (!isNew && existing.isError) {
    return (
      <StateBlock icon="users" title="User not found" tone="error">
        <Link to="/users">Back to User Access Level</Link>
      </StateBlock>
    )
  }

  return (
    <div className="w7-users-page w7-users-page--form">
      <PageHead
        title={isNew ? 'Create user' : `Edit ${username || 'user'}`}
        subtitle={
          <span className="w7-users-crumb">
            <Link to="/users">User Access Level</Link>
            <span aria-hidden="true"> / </span>
            {isNew ? 'New staff login' : 'Edit staff login'}
          </span>
        }
        actions={
          <div className="w7-users-actions">
            <Button variant="ghost" onClick={() => navigate('/users')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={saveDisabled}
              onClick={() => void onSave()}
            >
              {isNew ? 'Create user' : 'Save changes'}
            </Button>
          </div>
        }
      />

      <Banner tone="info">{ACCESS_LEVEL_BANNER}</Banner>

      <div className="w7-users-form-layout">
        <aside className="w7-users-aside" aria-label="Form outline">
          <ol className="w7-users-steps">
            <li>
              <span className="w7-users-step-n">1</span>
              <span>
                <strong>Account</strong>
                <em>Username, password, status</em>
              </span>
            </li>
            <li>
              <span className="w7-users-step-n">2</span>
              <span>
                <strong>Market scope</strong>
                <em>Bookmaker / session access</em>
              </span>
            </li>
            <li>
              <span className="w7-users-step-n">3</span>
              <span>
                <strong>Grants</strong>
                <em>View + each action</em>
              </span>
            </li>
          </ol>
        </aside>

        <div className="w7-users-form-main">
          <section className="w7-users-block" aria-labelledby="users-account-title">
            <header className="w7-users-block-head">
              <h2 id="users-account-title">1. Account</h2>
            </header>
            <div className="w7-users-fields">
              <Field
                label="Username"
                required={isNew}
                hint={
                  isNew && ownerUsername
                    ? `Login will be ${labPrefixedStaffUsername(ownerUsername, username || '…') || `${ownerUsername.toLowerCase()}.…`}`
                    : isNew
                      ? 'Owner login prefix is applied automatically (Lab Access Level).'
                      : 'Username is locked after create.'
                }
              >
                {({ id: fid, describedBy, invalid }) =>
                  isNew && ownerUsername ? (
                    <div className="w7-users-username-with-prefix">
                      <span className="w7-users-username-prefix" aria-hidden="true">
                        {ownerUsername.toLowerCase()}.
                      </span>
                      <input
                        id={fid}
                        aria-describedby={describedBy}
                        aria-invalid={invalid || undefined}
                        className="w7-input"
                        value={username}
                        autoComplete="off"
                        onChange={(e) =>
                          setUsername(e.target.value.replace(/\s/g, '').toLowerCase())
                        }
                        placeholder="suffix (e.g. abcdefg)"
                      />
                    </div>
                  ) : (
                    <input
                      id={fid}
                      aria-describedby={describedBy}
                      aria-invalid={invalid || undefined}
                      className="w7-input"
                      value={username}
                      disabled={!isNew}
                      autoComplete="off"
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter username"
                    />
                  )
                }
              </Field>
              <Field label="Status">
                {() => (
                  <Segmented
                    label="Status"
                    value={status}
                    onChange={setStatus}
                    items={[
                      { id: 'active', label: 'Active' },
                      { id: 'inactive', label: 'Inactive' },
                    ]}
                  />
                )}
              </Field>
              <Field
                label={isNew ? 'Password' : 'New password'}
                required={isNew}
                hint={!isNew ? 'Leave blank to keep the current password.' : undefined}
              >
                {({ id: fid, describedBy, invalid }) => (
                  <input
                    id={fid}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    className="w7-input"
                    type="password"
                    value={password}
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isNew ? 'At least 8 characters' : 'Optional'}
                  />
                )}
              </Field>
              <Field label="Confirm password" required={isNew || Boolean(password)}>
                {({ id: fid, describedBy, invalid }) => (
                  <input
                    id={fid}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    className="w7-input"
                    type="password"
                    value={confirm}
                    autoComplete="new-password"
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter password"
                  />
                )}
              </Field>
            </div>
          </section>

          <section className="w7-users-block" aria-labelledby="users-scope-title">
            <header className="w7-users-block-head">
              <h2 id="users-scope-title">2. Bookmaker / session access</h2>
            </header>
            <Field label="Market access" required>
              {({ id: fid, describedBy }) => (
                <select
                  id={fid}
                  aria-describedby={describedBy}
                  className="w7-select w7-users-select"
                  value={sessionFlag}
                  onChange={(e) =>
                    setSessionFlag(e.target.value as StaffUser['bookmakerSessionFlag'])
                  }
                >
                  {SESSION_FLAGS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </section>

          <section className="w7-users-block" aria-labelledby="users-access-title">
            <header className="w7-users-block-head">
              <h2 id="users-access-title">3. Assign access</h2>
            </header>
            {roleslist.isLoading ? (
              <StateBlock icon="users" title="Loading grants…" />
            ) : (
              <ul className="w7-users-menus">
                {menus.map((menu) => (
                  <li key={menu.menu} className="w7-users-menu-block">
                    <h3 className="w7-users-menu-title">{menu.menu}</h3>
                    <ul className="w7-users-roles">
                      {menu.roles.map((role) => (
                        <li key={`${menu.menu}:${role.accessRole}`} className="w7-users-role-block">
                          <div className="w7-users-role-head">
                            <strong>{role.accessRole}</strong>
                          </div>
                          <div className="w7-users-grant-grid">
                            {role.actions.map((a) => {
                              const key = featureKey(role.accessRole, a.label)
                              return (
                                <label
                                  key={`${role.accessRole}:${a.label}:${a.code}`}
                                  className="w7-users-grant"
                                >
                                  <Checkbox
                                    checked={selected.includes(key)}
                                    label={a.label}
                                    onChange={(on) => setFeature(role.accessRole, a.label, on)}
                                  />
                                  <span>{a.label}</span>
                                </label>
                              )
                            })}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <footer className="w7-users-form-foot">
            <Button variant="ghost" onClick={() => navigate('/users')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={saveDisabled}
              onClick={() => void onSave()}
            >
              {isNew ? 'Create user' : 'Save changes'}
            </Button>
          </footer>
        </div>
      </div>
    </div>
  )
}
